<template>
  <div class="container" :class="{ 'failed-excel-import': this.currentStep == 'excelError' }">
    <section class="bd-main businessDetails">
      <div class="dialog-container dialog-light">
        <div
          class="dialog dialog-md"
          :class="{
            'chart-of-accounts-dialog' : this.currentStep === 'reviewImport' && this.importType === 'skipped',
            'review-excel-dialog' : this.currentStep === 'reviewImport' && this.importType !== 'skipped'
          }"
        >
        <!-- Organisation selection -->
        <organisation-set-up-dialog
            v-if="this.currentStep==='orgTypeSelection'"
            :allow-cancel="allowCancel"
            :selected-option="selectedOrgType"
            @onCancel="tryCloseMenu()"
            @createSinchiiOrg="() => showBusinessStep(true)"
          />
          <!-- Business setup -->
          <business-set-up-dialog
            v-if="this.currentStep==='businessDetails'"
            :businessDetails="this.businessDetails"
            @onBack="showSelectionStep()"
            @onNext="showImportStep()"
          />
          <!-- import setup -->
          <import-set-up-dialog
            v-if="this.currentStep==='importSelection'"
            ref="importSetUp"
            @onBack="showBusinessStep()"
            @skipImportSelected="skipImportSelected"
            @excelImportSelected="excelImportSelected"
            @showUnsuccessfulImportExcel="showUnsuccessfulImportExcel"
          />
          <!-- Chart of Accounts -->
          <div
            class="py-0"
            :class="{
              'd-none' : this.hideCoAScreen
            }"
            id="chartOfAccountsBody"
          >
            <chart-of-accounts
              :signUp="true"
              ref="coa"
              @onSubmit="finaliseAccount"
              @closePopup="showImportStep"
              :accounts="accounts"
            />
          </div>
          <!-- Review import -->
          <div
            class="py-0"
            :class="{
              'd-none' : this.hideReviewScreen
            }"
          >
            <review-import
              :typeImport="this.reviewImportType"
              :data="this.reviewImportType === 'excel' ? importExcelData : importServiceData"
              @onNext="finaliseAccount"
              @onBack="() => showImportStep(true)"
            />
          </div>
          <!-- Unsuccessful import excel -->
          <div class="py-0" v-if="this.currentStep == 'excelError'">
            <unsuccessful-import-excel
              :errorData="this.errorOnImportExcel"
              @reupload="showImportStep()"
            />
          </div>
        </div>
        <!-- dots / at the end of container-->
        <div class="dialog dialog-md" v-if="showDialogFooter && currentStep != 'finalised'">
          <div class="dialog-footer">
            <div class="dots pt-3">
              <span class="dot" :class="{'active-dot': this.currentStep == 'orgTypeSelection'}" @click="(completedSteps['orgTypeSelection'] ? showSelectionStep() : null)"></span>
              <span class="dot" :class="{'active-dot': this.currentStep == 'businessDetails'}" @click="(completedSteps['businessDetails'] ? showBusinessStep() : null)"></span>
              <span v-if="this.orgType != 'service'" class="dot" :class="{'active-dot': this.currentStep == 'importSelection'}" @click="(completedSteps['importSelection'] ? showImportStep() : null)"></span>
              <span class="dot" :class="{'active-dot': this.currentStep == 'reviewImport' || this.currentStep == 'excelError'}"></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading Spinner (two versions since this component runs in two very different contexts) -->
      <loading-dialog :text="'Creating your new organisation'" v-if="currentStep=='finalised' && allowCancel" />
      <ax-shared-popup
        v-else
        :size="'md'"
        :id="'finalSync'"
        ref="finalSync"
      >
        <template #body>
          <loading-dialog :text="'Creating your new organisation'" v-if="currentStep=='finalised'" />
        </template>
      </ax-shared-popup>
    </section>
  </div>
</template>

<script>
import store from "../store";
import OrganisationSetUp from "../components/dialogs/OrganisationSetUp.vue";
import BusinessSetUp from "../components/dialogs/BusinessSetUp.vue";
import ImportSetUp from "../components/dialogs/ImportSetUp.vue";
import ChartOfAccounts from "../components/dialogs/ChartOfAccounts.vue";
import ReviewImport from "../components/dialogs/ReviewImportDialog.vue";
import UnsuccessfulImportExcel from "../components/dialogs/UnsuccessfulImportExcel.vue";
import LoadingDialog from "../components/dialogs/SyncDialog.vue"
import BusinessService from "../services/business-service.ts"
import { useToast } from "vue-toastification";
import businessService from '../services/business-service';

const toast = useToast();

export default {
  name: "organisation-wizard",
  components: {
    "organisation-set-up-dialog": OrganisationSetUp,
    "business-set-up-dialog": BusinessSetUp,
    "import-set-up-dialog": ImportSetUp,
    "chart-of-accounts": ChartOfAccounts,
    "review-import": ReviewImport,
    "unsuccessful-import-excel": UnsuccessfulImportExcel,
    "loading-dialog": LoadingDialog
  },
  props: {
    serviceToken: {
      default: null,
      type: Object
    },
    serviceTenant: {
      default: null,
      type: Object
    },
    allowCancel: {
      default: true,
      type: Boolean
    }
  },
  data() {
    return {
      orgType: null,
      importType: null,
      currentStep: null,
      businessDetails: {},
      importServiceData: null,
      importExcelData: null,
      accounts: [],
      errorOnImportExcel: null,
      showDialogFooter: true,
      serviceConnected: false,
      completedSteps: [],
      isReview: false
    }
  },
  computed: {
    reviewImportType() {
      return this.orgType == "service" ? "xero" : "excel";
    },
    hideCoAScreen() {
      return this.currentStep != 'reviewImport' || this.importType != 'skipped'
    },
    hideReviewScreen() {
      return this.currentStep != 'reviewImport' || this.importType == 'skipped'
    },
    selectedOrgType() {
      return { value: this.orgType };
    }
  },
  async beforeCreate() {
    await store.dispatch("initialise");
  },
  async mounted() {
    if (this.serviceToken) {
      var regionResponse = await businessService.checkSupportedRegion({
        token: this.serviceToken,
        tenantID: this.serviceTenant.tenantId
      });

      if (regionResponse?.supported ?? false) {
        this.orgType = "service";
        
        this.businessDetails = {
          businessName: this.serviceTenant.tenantName,
          gstExclusive: false,
          otherIncomeHasGST: false,
        };
  
        this.completedSteps["orgTypeSelection"] = true;
        this.showBusinessStep();
      }
      else {
        toast.clear();
        toast.error("Cannot link xero organisation " + this.serviceTenant.tenantName + ", this organisation is from a region that is not currently supported by Sinchii.");

        this.businessDetails = {
          gstExclusive: false,
          otherIncomeHasGST: false,
        };

        this.showSelectionStep();
      }
    }
    else {
      this.businessDetails = {
        gstExclusive: false,
        otherIncomeHasGST: false,
      };

      this.showSelectionStep();
    }
  },
  methods: {
    tryCloseMenu() {
      if (this.allowCancel) {
        this.$emit("onCancel");
      }
    },
    showSelectionStep() {
      this.updateCurrentStep("orgTypeSelection");
    },
    showBusinessStep(userSelection) {
      if (userSelection && this.orgType == "service") {
        this.orgType = 'sinchii';
        this.completedSteps = []; // Reset steps, as we're now changing org type
        this.businessDetails = {};
      }

      this.updateCurrentStep("businessDetails");
      this.showDialogFooter = true;
    },
    async showImportStep(back) {
      this.isReview = false;
      if (this.orgType === "service") {
        this.importType = "service";
        
        if (back) {
          this.showBusinessStep();
        }
        else {
          if (await this.getSignupServiceData(this.serviceToken, this.serviceTenant)) {
            this.finaliseAccount();
          }
          else {
            this.completedSteps = [];
            this.showSelectionStep();
          }
        }
      }
      else {
        this.updateCurrentStep("importSelection");
        this.showDialogFooter = true;
      }
    },
    showReviewScreen() {
      this.updateCurrentStep("reviewImport");
      this.showDialogFooter = false;
    },
    showUnsuccessfulImportExcel(error) {
      this.errorOnImportExcel = error;
      this.updateCurrentStep("excelError");
      this.showDialogFooter = false;
    },
    async skipImportSelected() {
      this.importType = "skipped";
      await this.getAccounts();
      this.$refs.coa.reloadTriggerPointerEvent();
      this.showLoadingStep();
      this.finaliseAccount();
    },
    excelImportSelected(data) {
      this.importType = "excel";
      this.showReviewScreen();
      
      data.forEach(item => {
        item.expenseAccountType = {
          expenseAccountTypeID: item.typeID,
          expenseAccountDescription: item.type
        }
      });
          
      this.importExcelData = data;
    },
    showLoadingStep() {
      this.updateCurrentStep("finalised")
    },
    updateCurrentStep(step) {
      this.currentStep = step;
      this.completedSteps[step] = true;

      if (this.currentStep == "orgTypeSelection") {
        this.$emit("onSizeChanged", "md");
      }
      else if (this.currentStep == "businessDetails") {
        this.$emit("onSizeChanged", "md");
      }
      else if (this.currentStep == "importSelection") {
        this.$emit("onSizeChanged", "md");
      }
      else if (this.currentStep == "reviewImport") {
        this.$emit("onSizeChanged", "xl");
      }
      else if (this.currentStep == "excelError") {
        this.$emit("onSizeChanged", "xl");
      }
      else {
        this.$emit("onSizeChanged", "md");
      }

      if (!this.allowCancel && step == 'finalised') {
        this.$refs.finalSync.open(); // Hack
      }
    },
    async getAccounts() {
      this.accounts = [];
      await BusinessService.getExpenseAccounts(true, true)
        .then((data) => {
          this.accounts = data.accounts;
          this.accounts.forEach(x => {
            x.ableToHide = true;
            x.ableToDelete = true;
          });
        }).catch(this.showAlert);
    },
    async getSignupServiceData() {
      try {
        const response = await BusinessService.getServiceReviewData({
          businessDetails: this.fillBusinessDetails(),
          token: this.serviceToken,
          tenantID: this.serviceTenant.tenantId
        });

        if (response && response.success) {
          if (response.accountsList != null) {
            this.importServiceData = businessService.mapReviewDataToViewModels(response.accountsList);
          }
          return true;
        }
        else {
          await this.resetXeroConnection();
          return false;
        }
      }
      catch (ex) {
        console.log(ex);
        await this.resetXeroConnection();
        return false;
      }
    },
    async resetXeroConnection() {
      toast.clear();
      toast.error("Something went wrong when fetching Xero data, please try again later.");

      // Remove the xero connection
      if (this.serviceToken && this.serviceTenant) {
        await BusinessService.removeXeroConnection({
          token: this.serviceToken,
          tenantID: this.serviceTenant.tenantId
        });
      }
    },
    showAlert() {
      this.showAlert("Something went wrong, please try again later.");
    },
    showAlert(message) {
      toast.clear();
      toast.error(message);
    },
    async finaliseAccount() {
      var reviewData;
      if (this.importType == "skipped") {
        reviewData = this.getReviewDataForCoA();
      }
      else if (this.importType == "excel") {
        reviewData = this.getReviewDataForExcel();
      }
      else if (this.importType == "service") {
        reviewData = this.getReviewDataForXero();
        if (!this.isReview) {
          this.isReview = true;
          this.showReviewScreen();
          return;
        }
      }
      else {
        this.showAlert();
      }

      var business = this.fillBusinessDetails();

      try {
        var result = await BusinessService.completeSignup({
          businessDetails: business,
          reviewData: reviewData,
          xeroToken: this.serviceToken
        });


        if (result?.data?.showReview) {
          this.showReviewScreen();
        } else {
          this.showLoadingStep();
          this.$emit("onFinalised", result.data.businessID);
        }
      }
      catch(error) {
        this.showAlert(error.message);
      }
    },
    fillBusinessDetails() {
      return {
        businessID: -1,
        businessName: this.businessDetails.businessName.trim(),
        businessIndustryID: this.businessDetails.businessIndustry.businessIndustryID,
        businessYearStructureID: 1,
        currentMonth: this.businessDetails.startMonth.month,
        gstExclusive: this.businessDetails.gstExclusive ?? false,
        startMonth: this.businessDetails.startMonth.month,
        xeroTenantID: this.serviceTenant?.tenantId,
        xeroTenantName: this.serviceTenant?.tenantName,
        otherIncomeHasGST: this.businessDetails.otherIncomeHasGST ?? false,
      };
    },
    getReviewDataForCoA() {
      const mappedResults = this.accounts.map((x) => {
        return {
          name: x.description,
          gstInclusive: x.gstInclusive,
          accountType: x.expenseAccountType.expenseAccountTypeID,
          previousYearAmount: 0,
          yearToDateAmount: 0,
          hidden: false
        }
      });

      return mappedResults;
    },
    getReviewDataForExcel() {
      const mappedResults = this.importExcelData.map((x) => {
        return {
          name: x.name,
          gstInclusive: x.includesGST,
          accountType: x.typeID,
          yearBeforeLastAmount: x.yearBeforeLastAmount,
          previousYearAmount: x.previousYearAmount,
          yearToDateAmount: x.yearToDateAmount,
          hidden: false
        }
      });

      return mappedResults;
    },
    getReviewDataForXero() {
      const mappedResults = this.importServiceData.map((x) => {
        return {
          name: x.name,
          gstInclusive: x.includesGST,
          accountType: x.expenseAccountType.expenseAccountTypeID,
          previousYearAmount: x.previousYearAmount,
          yearToDateAmount: x.yearToDateAmount,
          hidden: x.hidden
        }
      });

      return mappedResults;
    }
  }
}
</script>
